import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import OpenInNewOutlinedIcon from "@mui/icons-material/OpenInNewOutlined";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormGroup from "@mui/material/FormGroup";
import FormLabel from "@mui/material/FormLabel";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { isApiError, kioskStaffApi } from "@bw/shared/api";
import { QrCode } from "@bw/shared/components";
import type {
  KioskDeviceCreated,
  KioskPurpose,
} from "@bw/shared/types/generated";
import { buildKioskSetupUrl } from "@bw/shared/utils";
import { useEffect, useMemo, useState } from "react";

import { KIOSK_PURPOSE_LABEL } from "./kiosk-display";

const PURPOSES: KioskPurpose[] = ["drop_in", "booking"];

export function kioskErrorMessage(cause: unknown): string {
  if (isApiError(cause)) {
    if (cause.code === "kiosk_off") return "Turn kiosk check-in on first.";
    if (cause.status === 403)
      return "You can't manage kiosks at this facility.";
    if (cause.message) return cause.message;
  }
  return "That didn't go through. Try again.";
}

function useSecondsLeft(expiresAt: string | null): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!expiresAt) return;
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [expiresAt]);
  if (!expiresAt) return 0;
  return Math.max(0, Math.floor((new Date(expiresAt).getTime() - now) / 1000));
}

function formatCountdown(seconds: number): string {
  const minutes = Math.floor(seconds / 60);
  return `${minutes}:${String(seconds % 60).padStart(2, "0")}`;
}

/**
 * The one-time setup code for a kiosk, shown once: a QR of the pairing link
 * (or of the code when this origin has no known player app), the code as text
 * and a link to open the kiosk on this device. Valid for 15 minutes.
 */
export function KioskSetupCode({
  created,
  timeFormatter,
}: {
  created: KioskDeviceCreated;
  timeFormatter: Intl.DateTimeFormat;
}) {
  const link = useMemo(
    () => buildKioskSetupUrl(window.location.origin, created.setup_code),
    [created.setup_code],
  );
  const secondsLeft = useSecondsLeft(created.setup_expires_at);
  const expired = secondsLeft === 0;
  if (expired) {
    return (
      <Alert severity="warning">
        This setup code expired. Use Re-pair on {created.device.name} for a new
        one.
      </Alert>
    );
  }

  return (
    <Box sx={{ display: "grid", gap: 1.5 }}>
      <Typography variant="body2">
        On the tablet, open the kiosk, tap Scan setup code and hold this code
        up to its camera. Or, on the tablet itself, open the link. It works
        once.
      </Typography>
      <Box
        role="img"
        aria-label={`Setup QR for ${created.device.name}`}
        sx={{
          justifySelf: "center",
          lineHeight: 0,
          border: 1,
          borderColor: "divider",
          borderRadius: 1,
        }}
      >
        <QrCode value={link ?? created.setup_code} size={196} />
      </Box>
      <Typography variant="caption" color="text.secondary" role="timer">
        Expires at {timeFormatter.format(new Date(created.setup_expires_at))} ·{" "}
        {formatCountdown(secondsLeft)} left
      </Typography>
      {link ? (
        <Button
          href={link}
          variant="outlined"
          color="inherit"
          startIcon={<OpenInNewOutlinedIcon fontSize="small" />}
          sx={{ justifySelf: "start", borderColor: "divider" }}
        >
          Open kiosk on this device
        </Button>
      ) : null}
    </Box>
  );
}

/**
 * Start scan kiosk (#2691): name the device and what it checks in for, then
 * pair the tablet with the one-time setup code.
 */
export function StartKioskDialog({
  open,
  facilityId,
  facilityName,
  timeFormatter,
  onClose,
  onCreated,
}: {
  open: boolean;
  facilityId: string;
  facilityName: string;
  timeFormatter: Intl.DateTimeFormat;
  onClose: () => void;
  onCreated: () => void;
}) {
  const [name, setName] = useState("Entrance tablet");
  const [purposes, setPurposes] = useState<KioskPurpose[]>([
    "drop_in",
    "booking",
  ]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [created, setCreated] = useState<KioskDeviceCreated | null>(null);
  // One key per dialog: a retry after a lost response returns the same kiosk.
  const [idempotencyKey, setIdempotencyKey] = useState(() =>
    crypto.randomUUID(),
  );

  useEffect(() => {
    if (open) {
      setName("Entrance tablet");
      setPurposes(["drop_in", "booking"]);
      setError(null);
      setCreated(null);
      setIdempotencyKey(crypto.randomUUID());
    }
  }, [open]);

  const trimmed = name.trim();
  const valid =
    trimmed.length > 0 && trimmed.length <= 60 && purposes.length > 0;

  const toggle = (purpose: KioskPurpose) =>
    setPurposes((current) =>
      current.includes(purpose)
        ? current.filter((p) => p !== purpose)
        : PURPOSES.filter((p) => p === purpose || current.includes(p)),
    );

  const submit = async () => {
    if (!valid || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const result = await kioskStaffApi.createDevice(facilityId, {
        name: trimmed,
        purposes,
        idempotency_key: idempotencyKey,
      });
      setCreated(result);
      onCreated();
    } catch (cause) {
      setError(kioskErrorMessage(cause));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle sx={{ typography: "subtitle1", fontWeight: 700 }}>
        {created ? `Pair ${created.device.name}` : "Start scan kiosk"}
      </DialogTitle>
      <DialogContent dividers sx={{ display: "grid", gap: 2 }}>
        {created ? (
          <KioskSetupCode created={created} timeFormatter={timeFormatter} />
        ) : (
          <>
            <TextField
              label="Facility"
              value={facilityName}
              size="small"
              slotProps={{ input: { readOnly: true } }}
            />
            <TextField
              label="Device name"
              value={name}
              size="small"
              onChange={(event) => setName(event.target.value)}
              error={trimmed.length === 0 || trimmed.length > 60}
              helperText={
                trimmed.length > 60 ? "60 characters at most" : undefined
              }
              slotProps={{ htmlInput: { maxLength: 80 } }}
            />
            <Box component="fieldset" sx={{ border: 0, m: 0, p: 0 }}>
              <FormLabel
                component="legend"
                sx={{ typography: "body2", mb: 0.5 }}
              >
                Checks in for
              </FormLabel>
              <FormGroup row>
                {PURPOSES.map((purpose) => (
                  <FormControlLabel
                    key={purpose}
                    control={
                      <Checkbox
                        size="small"
                        checked={purposes.includes(purpose)}
                        onChange={() => toggle(purpose)}
                      />
                    }
                    label={KIOSK_PURPOSE_LABEL[purpose]}
                  />
                ))}
              </FormGroup>
            </Box>
            <Box
              sx={{
                display: "flex",
                gap: 1,
                p: 1.25,
                borderRadius: 1,
                border: 1,
                borderColor: "divider",
                bgcolor: "action.hover",
              }}
            >
              <LockOutlinedIcon
                fontSize="small"
                sx={{ color: "text.secondary", mt: 0.25 }}
              />
              <Typography variant="body2" color="text.secondary">
                The tablet gets its own kiosk sign-in, not yours. It runs locked
                and full screen, never takes payment, and only accepts paid or
                redeemed codes. Revoke it any time in Devices.
              </Typography>
            </Box>
            {error ? <Alert severity="error">{error}</Alert> : null}
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 1.5 }}>
        {created ? (
          <Button variant="contained" onClick={onClose}>
            Done
          </Button>
        ) : (
          <>
            <Button color="inherit" onClick={onClose} sx={{ mr: "auto" }}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={() => void submit()}
              disabled={!valid || submitting}
            >
              {submitting ? "Starting…" : "Start kiosk"}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
